import { ActionTree } from 'vuex';
import ApiUrl from '../../../../api/url';
import { NodeOtherAccountState } from './types';
import { RootState } from '../../../types';
import * as types from './mutation-types';
import { AppContext } from '../../../context';
const MessageKey = AppContext.MessageKey;
import { translator } from '../../../translator';
import { isHappenedOtherPartyError } from '../../../error-type';
import router from '../../../../router';
import ScreenUrl from '../../../../router/url';

export const actions: ActionTree<NodeOtherAccountState, RootState> = {
    /**
     * ノード紐付きその他アカウント一覧取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} id ノードID
     * @param {any} pageNumber ページ番号
     * @param {any} perPage ページ毎の表示件数
     * @returns {Promise<void>}
     */
    async search({ dispatch, commit }, { id, pageNumber, perPage, sort } = {}) {
        const requestParams = {
            page_number: pageNumber,
            per_page: perPage,
            'sort[column]': sort.column,
            'sort[direction]': sort.direction,
        };
        const nodeId = id;
        try {
            commit(types.SEARCH_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.Node.OtherAccount.SEARCH.replace(':id', nodeId.toString()),
                    params: requestParams,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SEARCH_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SEARCH_FAILURE, resData.errors[0]);
        }
    },

    /**
     * その他アカウント追加
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} node_id ノードID
     * @param {otherAccount} otherAccount 追加その他アカウント
     * @returns {Promise<void>}
     */
    async create({ dispatch, commit, rootState }, { nodeId, otherAccount }) {
        try {
            commit(types.CREATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'post',
                    url: ApiUrl.Node.OtherAccount.CREATE.replace(':id', nodeId.toString()),
                    params: otherAccount,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.CREATE_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.OtherAccount.CREATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.CREATE_FAILURE, params);
        }
    },

    /**
     * その他アカウントの詳細情報取得
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} nodeId ノードID
     * @param {any} accountId その他アカウントID
     */
    async show({ dispatch, commit }, { nodeId, accountId }) {
        try {
            commit(types.SHOW_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'get',
                    url: ApiUrl.Node.OtherAccount.SHOW.replace(':id', nodeId.toString()).replace(
                        ':account_id',
                        accountId.toString()
                    ),
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.SHOW_SUCCESS, resData);
            }
        } catch (resData) {
            // アプリケーションエラー時
            commit(types.SHOW_FAILURE, resData.errors[0]);
        }
    },

    /**
     * その他アカウント更新
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} otherAccount 更新するその他アカウント情報
     * @param {any} nodeId ノードID
     * @param {any} accountId その他アカウントID
     * @returns {Promise<void>}
     */
    async update({ dispatch, commit, rootState }, { otherAccount, nodeId, accountId } = {}) {
        try {
            commit(types.UPDATE_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'put',
                    url: ApiUrl.Node.OtherAccount.UPDATE.replace(':id', nodeId.toString()).replace(
                        ':account_id',
                        accountId.toString()
                    ),
                    params: otherAccount,
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.UPDATE_SUCCESS);

                // 成功時トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.OtherAccount.UPDATED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.UPDATE_FAILURE, params);
        }
    },

    /**
     * その他アカウント除外
     * @param {any} dispatch Vuex変数
     * @param {any} commit Vuex変数
     * @param {any} rootState Vuex変数
     * @param {any} nodeId ノードID
     * @param {any} accountId 除外するその他アカウントID
     * @returns {Promise<void>}
     */
    async destroy({ dispatch, commit, rootState }, { nodeId, accountId }) {
        try {
            commit(types.DESTROY_START);
            const resData = await dispatch(
                'auth/api',
                {
                    method: 'delete',
                    url: ApiUrl.Node.OtherAccount.DESTROY.replace(':id', nodeId.toString()),
                    params: { account_id: accountId },
                },
                { root: true }
            );
            if (isHappenedOtherPartyError(resData)) {
                // システムエラー・予期せぬエラー時
                commit(types.FATAL_FAILURE);
            } else {
                // 成功時
                commit(types.DESTROY_SUCCESS);

                // 成功トースト表示
                const i18n = (rootState as any).i18n;
                const message = translator(i18n, MessageKey.Node.OtherAccount.DESTROYED);
                dispatch('toastMessage', message, { root: true });
            }
        } catch (resData) {
            // アプリケーションエラー時
            const params = {
                errorTitle: resData.title,
                errorDetails: resData.errors,
            };
            commit(types.DESTROY_FAILURE, params);
        }
    },

    /**
     * 検索済みデータをクリアする
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearWork({ commit }) {
        commit(types.CLEAR_WORK);
    },

    /**
     * 画面切り替え時のstoreの消去
     * @param {any} commit Vuex変数
     * @returns {Promise<void>}
     */
    async clearOnBeforeRoute({ commit }) {
        commit(types.CLEAR_ALL);
    },
};
